import type { CSSProperties } from "react";
import { MARKET_LABELS, type Market, type Signal } from "@shared/schema/signal";
import { directionGlyph, formatTime, rankSignals } from "../lib";

function briefDate(timestamp: string): string {
  if (!timestamp) return "数据更新中";
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
  }).format(new Date(timestamp));
}

function signalIcon(signal: Signal): string {
  if (signal.category === "volatility") return "⌁";
  if (signal.category === "macro") return "▥";
  return directionGlyph(signal.direction);
}

export function MorningBrief({
  signals,
  allSignals,
  generatedAt,
}: {
  signals: Signal[];
  allSignals: Signal[];
  generatedAt: string;
}) {
  const ranked = rankSignals(signals);
  const latestDay = allSignals.map((signal) => signal.timestamp.slice(0, 10)).sort().at(-1) ?? "";
  const counts = {
    added: signals.filter((signal) => signal.timestamp.startsWith(latestDay)).length,
    important: signals.filter((signal) => signal.strength >= 4).length,
    persistent: signals.filter((signal) => signal.persistence >= 3).length,
  };
  const markets = [...new Set(allSignals.map((signal) => signal.market))] as Market[];
  const freshCount = allSignals.filter((signal) => signal.status === "fresh").length;
  const completion = allSignals.length === 0 ? 0 : Math.round((freshCount / allSignals.length) * 100);
  const completionStyle = { "--brief-completion": `${completion * 3.6}deg` } as CSSProperties;

  return (
    <section className="morning-view view-enter" aria-labelledby="brief-title">
      <div className="brief-masthead">
        <div>
          <span className="eyebrow">MORNING BRIEF</span>
          <h1 id="brief-title">晨间简报</h1>
        </div>
        <div className="brief-date">
          <strong>{briefDate(generatedAt)}</strong>
          <span>只描述指标相对自身历史的变化</span>
        </div>
      </div>

      <div className="brief-dashboard">
        <section className="brief-card brief-overview" aria-labelledby="overview-title">
          <h2 id="overview-title">今日概览</h2>
          <div className="overview-body">
            <div className="freshness-ring" style={completionStyle} aria-label={`${freshCount} / ${allSignals.length} 个指标数据新鲜`}>
              <div><strong>{freshCount}</strong><span>/{allSignals.length}</span></div>
              <small>数据新鲜</small>
            </div>
            <div className="overview-copy">
              <strong>{counts.added} 项新增信号</strong>
              <p><span>{counts.important}</span> 项重要变化</p>
              <p><span>{counts.persistent}</span> 项持续信号</p>
              <small>信号是历史异常描述，不是风险评分</small>
            </div>
          </div>
        </section>

        <section className="brief-card brief-key-signals" aria-labelledby="key-signals-title">
          <div className="brief-card-title">
            <h2 id="key-signals-title">关键信号</h2>
            <span>按异常程度排序</span>
          </div>
          <div className="key-signal-list">
            {ranked.slice(0, 3).map((signal) => (
              <article key={signal.id}>
                <span className={`key-signal-icon direction-${signal.direction}`}>{signalIcon(signal)}</span>
                <div><strong>{signal.title}</strong><small>{MARKET_LABELS[signal.market]} · {signal.displayValue}</small></div>
                <span className={`key-signal-direction direction-${signal.direction}`}>{directionGlyph(signal.direction)}</span>
              </article>
            ))}
            {ranked.length === 0 ? <p className="empty-state">当前筛选下没有信号。</p> : null}
          </div>
        </section>

        <section className="brief-card brief-timeline" aria-labelledby="timeline-title">
          <div className="brief-card-title">
            <h2 id="timeline-title">信号时间线</h2>
            <span>最近更新</span>
          </div>
          <ol>
            {[...signals].sort((a, b) => b.timestamp.localeCompare(a.timestamp)).slice(0, 6).map((signal, index) => (
              <li key={signal.id} className={index === 0 ? "is-latest" : ""}>
                <time>{formatTime(signal.timestamp)}</time>
                <span>{signal.title}</span>
                <small>{MARKET_LABELS[signal.market]}</small>
              </li>
            ))}
          </ol>
        </section>

        <section className="brief-card brief-market-snapshot" aria-labelledby="snapshot-title">
          <div className="brief-card-title">
            <h2 id="snapshot-title">市场快照</h2>
            <span>数据覆盖状态</span>
          </div>
          <div className="market-snapshot-grid">
            {markets.map((market) => {
              const items = allSignals.filter((signal) => signal.market === market);
              const marketFresh = items.filter((signal) => signal.status === "fresh").length;
              const lead = rankSignals(items)[0];
              return (
                <article key={market}>
                  <span>{MARKET_LABELS[market]}</span>
                  <strong>{marketFresh}<small>/{items.length}</small></strong>
                  <p>指标已更新</p>
                  {lead ? <em className={`direction-${lead.direction}`}>{directionGlyph(lead.direction)} {lead.title}</em> : null}
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </section>
  );
}
